import { ArrowsRightLeftIcon, EyeIcon } from '@heroicons/react/24/outline';

import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';
import { isUserDefinedFilter, SavedBrowseFilter } from '@/pages/browse/hooks/useFiltersService';
import { BrowseActiveFilter, FILTER_TYPE } from '@/pages/browse/types';

import { FilterMenuItem } from './FilterMenuItem';
import { decodeItemQuery, matchesAnyText } from './filterQuery';

interface FilterListSavedProps {
    filters: SavedBrowseFilter[];
    query: string;
    activeFilters: Record<string, BrowseActiveFilter>;
    onSelect: (type: string, filter: BrowseActiveFilter) => void;
}

function savedFilterTitleKey(filter: SavedBrowseFilter): string {
    if (isUserDefinedFilter(filter)) {
        if (filter.readOnly) {
            return 'ITEMS_SEARCH_FILTER.PROVIDED_BY_OTHER';
        }
        return filter.isPublic
            ? 'ITEMS_SEARCH_FILTER.PROVIDED_BY_YOU'
            : 'ITEMS_SEARCH_FILTER.CREATED_BY_YOU';
    }
    return 'ITEMS_SEARCH_FILTER.PROVIDED_BY_DATAMODEL';
}

export function FilterListSaved({
    filters,
    query,
    activeFilters,
    onSelect,
}: FilterListSavedProps) {
    const { t } = useTranslation();

    const visible = filters.filter((filter) =>
        matchesAnyText(query, filter.label, filter.name),
    );

    if (visible.length === 0) {
        return null;
    }

    return (
        <>
            {visible.map((filter) => {
                const active = activeFilters[filter.type];
                const filterId = isUserDefinedFilter(filter) ? filter.id : undefined;
                const isActive = active?.name === filter.name
                    && (active?.id == null || filterId == null || active.id === filterId);

                return (
                    <li key={`${filter.type}-${filter.name}`} className="tw-list-none">
                        <FilterMenuItem
                            title={t(savedFilterTitleKey(filter))}
                            isActive={isActive}
                            onSelect={() => {
                                if (isUserDefinedFilter(filter)) {
                                    const keyword = filter.itemsQuery?.keyword
                                        ? decodeItemQuery(filter.itemsQuery.keyword)
                                        : undefined;
                                    onSelect(FILTER_TYPE.USER_DEFINED, {
                                        name: filter.name,
                                        type: FILTER_TYPE.USER_DEFINED,
                                        id: filter.id,
                                        readOnly: filter.readOnly,
                                        isPublic: filter.isPublic,
                                        itemsQuery: keyword ? { keyword } : filter.itemsQuery,
                                    });
                                    return;
                                }

                                onSelect(FILTER_TYPE.DATA_MODEL_DEFINED, {
                                    name: filter.label || filter.name,
                                    type: FILTER_TYPE.DATA_MODEL_DEFINED,
                                    itemsQuery: {
                                        keyword: decodeItemQuery(`(${filter.query})`),
                                    },
                                });
                            }}
                        >
                            <span className="tw-flex tw-items-center tw-gap-2">
                                {isUserDefinedFilter(filter) && filter.readOnly && (
                                    <Icon icon={ArrowsRightLeftIcon} size="xs" />
                                )}
                                {isUserDefinedFilter(filter) && filter.isPublic && !filter.readOnly && (
                                    <Icon icon={EyeIcon} size="xs" />
                                )}
                                <span>{filter.label || filter.name}</span>
                            </span>
                        </FilterMenuItem>
                    </li>
                );
            })}
        </>
    );
}
